/**
 * src/features/auth/ui/PasswordNudgeBanner.tsx
 *
 * Phase 43 (PWD-08) — a one-time dismissible dashboard nudge telling a magic-link user
 * that setting a password exists as an option. Mounted in DashboardPage, above the
 * tab-panel AnimatePresence (an account-level nudge, not scoped to any one tab).
 *
 * Show condition — ALL three must hold:
 *   1. getLastLoginMethod() === 'magic-link' (lastLoginMethod.ts, reused verbatim)
 *   2. the dismissed-sentinel key is absent from localStorage
 *   3. useAuthStore's hasPassword is EXACTLY `false` — not `true`, and not `null`
 *      ("not yet known" must suppress the nudge, never trigger it; a user who already
 *      has a password must never see even a flash of this banner before the plan fetch
 *      settles, since `hasPassword` starts `null` and the plan fetch is async).
 *
 * The two storage values are read ONCE in lazy state initializers (not in an effect) so
 * the FIRST painted frame is already correct — same AUTH-09 no-flash reasoning as
 * lastLoginMethod.ts's own consumers.
 *
 * Dismissing writes the sentinel and hides the banner immediately, no reload needed.
 * Following the call-to-action does NOT write the sentinel — moving toward the settings
 * card is not a refusal of the offer; the nudge can still reappear until the user either
 * sets a password or explicitly closes the banner.
 */

import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useReducedMotion } from '@/shared/lib/hooks/useReducedMotion';
import { useAuthStore } from '@/stores/useAuthStore';
import { getLastLoginMethod } from '../lib/lastLoginMethod';

/** Colon convention (matches LAST_LOGIN_METHOD_KEY in lastLoginMethod.ts), NOT the hyphen
 * convention PostExportPrompt.tsx itself uses for its own sentinel. Boolean sentinel '1'
 * — never a free-form string (CLAUDE.md rule 7). */
export const PASSWORD_NUDGE_DISMISSED_KEY = 'estimatix:password_nudge_dismissed';

function readDismissed(): boolean {
  try {
    return localStorage.getItem(PASSWORD_NUDGE_DISMISSED_KEY) !== null;
  } catch {
    return false;
  }
}

export interface PasswordNudgeBannerProps {
  /** Navigates the dashboard to the Settings tab (System) where SecuritySettingsCard
   * lives. Kept as a callback prop so this component stays free of dashboard state. */
  onNavigateToSettings: () => void;
}

export const PasswordNudgeBanner: FC<PasswordNudgeBannerProps> = ({ onNavigateToSettings }) => {
  const { t } = useTranslation();
  const reduced = useReducedMotion();
  const hasPassword = useAuthStore((s) => s.hasPassword);

  const [dismissed, setDismissed] = useState(readDismissed);
  const [lastMethod] = useState(getLastLoginMethod);

  const visible = !dismissed && lastMethod === 'magic-link' && hasPassword === false;

  const handleDismiss = () => {
    try {
      localStorage.setItem(PASSWORD_NUDGE_DISMISSED_KEY, '1');
    } catch {
      // Swallowed intentionally — a storage failure degrades the nudge, it never
      // blocks the dashboard (T-43-17).
    }
    setDismissed(true);
  };

  // Early return (not just an AnimatePresence-internal condition) so dismissing hides the
  // banner immediately, synchronously, without waiting on an exit transition — same
  // convention as PostExportPrompt.tsx's own visible/dismissed guard.
  if (!visible) {
    return null;
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          role="complementary"
          aria-labelledby="password-nudge-heading"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: reduced ? 0 : 0.2 }}
          className="rounded-2xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 p-4 mb-6 flex items-start gap-3"
        >
          <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-xl text-blue-600 dark:text-blue-400 shrink-0">
            <KeyRound size={20} aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <p
              id="password-nudge-heading"
              className="text-body font-bold text-slate-900 dark:text-slate-100"
            >
              {t('dashboard.password_nudge.title', 'Set a password for faster sign-in')}
            </p>
            <p className="text-body text-slate-600 dark:text-slate-400 mt-1">
              {t(
                'dashboard.password_nudge.body',
                "You signed in with an email link last time. Add a password so you don't need to check your email every time.",
              )}
            </p>
            <button
              type="button"
              onClick={onNavigateToSettings}
              className="mt-3 text-body font-bold text-blue-600 dark:text-blue-400"
            >
              {t('dashboard.password_nudge.cta', 'Set a password')}
            </button>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label={t('dashboard.password_nudge.dismiss_aria', 'Dismiss')}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors shrink-0"
          >
            <X size={14} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
